import {OpeningLogiCtrlStatus} from "../../../../../api/Openings";
import {Button} from "@mui/material";
import {useContext, useEffect, useState} from "react";
import {api} from "../../../../../api/API";
import {LogiCtrlRule, LogiCtrlStatus} from "../../../../../api/LogiCtrl";
import {useProjectId} from "../../ProjectName";
import {EditContext} from "../../../../../misc/scroller/EditProvider";
import {useBackgroundAction} from "../../../manage/sov/UseBackgroundTask";
import {Row} from "./Row";
import {UserRuleInstructions} from "./UserRuleInstructions";

export function OpeningsRulesTab(props: {
    opening: number;
    value: OpeningLogiCtrlStatus;
    onClose: () => void;
    onReload?(): void;
    ready: boolean;
    rules: LogiCtrlRule[];
    statuses: LogiCtrlStatus[]
}) {
    const [rules, setRules] = useState<LogiCtrlRule[]>([]);

    const serverRules = props.rules;
    useEffect(() => {
        setRules(serverRules);
    }, [serverRules]);

    const projectId = useProjectId();

    const [edited, setEdited] = useState(false);

    const onRuleChange = (old: LogiCtrlRule | null, updated: LogiCtrlRule) => {
        setEdited(true);

        setRules(list => {
            if(old) {
                return list.map(r => r === old ? updated : r);
            }

            return [...list, updated];
        })
    }

    const editCtx = useContext(EditContext);

    const save = useBackgroundAction({
        callback: async (input: Parameters<typeof api.logiCtrl.upsertRules>[0]) => {
            return api.logiCtrl.upsertRules(input);
        },
        onDone: () => {
            try {
                editCtx.reload();
            } catch (e) {}

            props.onReload?.();
            props.onClose();
        }
    });

    return (
        <div style={{padding: "6px 12px 12px 12px", display: "flex", flexDirection: "column", gap: 6, maxWidth: 550}}>
            <Row
                title="Doors" error={props.value.doorError} edited={props.value.doorEdited}
                skipRules={props.value.doorSkipRules}
                filter={{
                    opening: props.opening,
                    kind: "door",
                    project: projectId,
                }}
                rules={rules}
                statuses={props.statuses}
                ready={props.ready}
                onRuleChange={onRuleChange}
            />
            <Row
                title="Frames"
                error={props.value.frameError}
                edited={props.value.frameEdited}
                skipRules={props.value.frameSkipRules}
                ready={props.ready}
                filter={{
                    opening: props.opening,
                    kind: "frame",
                    project: projectId,
                }}
                rules={rules}
                statuses={props.statuses}
                onRuleChange={onRuleChange}
            />
            <Row
                title="Anchors"
                error={props.value.anchorsError}
                edited={props.value.anchorsEdited}
                skipRules={props.value.anchorsSkipRules}
                ready={props.ready}
                filter={{
                    opening: props.opening,
                    kind: "frame-anchor",
                    project: projectId,
                }}
                rules={rules}
                statuses={props.statuses}
                onRuleChange={onRuleChange}
            />
            <Row
                title="Hardware"
                filter={{
                    opening: props.opening,
                    kind: "hardware",
                    project: projectId,
                }}
                error={props.value.hardwareError}
                edited={props.value.hardwareEdited}
                skipRules={props.value.hardwareSkipRules}
                ready={props.ready}
                rules={rules}
                statuses={props.statuses}
                onRuleChange={onRuleChange}
            />
            {(props.value.glazing || []).map(g => (
                <Row
                    key={g.id}
                    title={g.marks ? `Glazing ${g.marks}` : "Glazing"}
                    filter={{
                        kind: "glazing",
                        glazingItem: g.id,
                        project: projectId,
                    }}
                    error={g.error}
                    edited={g.edited}
                    skipRules={g.skipRules}
                    ready={props.ready}
                    rules={rules}
                    statuses={props.statuses}
                    onRuleChange={onRuleChange}
                />
            ))}
            {edited && (
                <div style={{display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 4}}>
                    {save.LoadingElement}
                    <Button size="small" onClick={() => props.onClose()} disabled={save.loading} sx={{textTransform: "none", fontSize: 12}}>
                        Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={() => save.callback({
                        rules,
                        project: projectId,
                        opening: props.opening,
                        div10: null,
                        miscProduct: null,
                        glazing: null,
                    })} disabled={save.loading} sx={{textTransform: "none", fontSize: 12}}>
                        Save
                    </Button>
                </div>
            )}
            <UserRuleInstructions />
        </div>
    )
}
